import React, { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import type { JobApplication, JobApplicationStatus, JobPosting } from '../../../types/hiring';
import { applicationStatusTrailLabel } from '../../../utils/jobOfferLetter';
import AddCandidateModal from './AddCandidateModal';
import JobApplicationDetail from './JobApplicationDetail';

type Props = {
  applications: JobApplication[];
  postings: JobPosting[];
  newCount: number;
  filterPostingId: string;
  onFilterPostingIdChange: (id: string) => void;
  selectedApplicationId: string | null;
  onSelectApplicationId: (id: string | null) => void;
  reviewerUid: string;
  highlightTeamCommentId?: string | null;
  onTeamCommentConsumed?: () => void;
  highlightPdfCommentId?: string | null;
  onOpenResumePin?: (opts: { annotationId: string; pageNumber?: number }) => void;
  /** Finished hires are listed under Hired, not in this table. */
  excludeApplicationIds?: ReadonlySet<string>;
};

const SOURCE_LABELS: Record<JobApplication['source'], string> = {
  website: 'Website',
  manual: 'Manual',
};

const JobApplicationsPanel: React.FC<Props> = ({
  applications,
  postings,
  newCount,
  filterPostingId,
  onFilterPostingIdChange,
  selectedApplicationId,
  onSelectApplicationId,
  reviewerUid,
  highlightTeamCommentId,
  onTeamCommentConsumed,
  highlightPdfCommentId,
  onOpenResumePin,
  excludeApplicationIds,
}) => {
  const selected = applications.find(a => a.id === selectedApplicationId) || null;
  const [addOpen, setAddOpen] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const [statusOverrides, setStatusOverrides] = useState<Record<string, JobApplicationStatus>>({});
  const statusOf = (app: JobApplication) => statusOverrides[app.id] || app.status;
  const rejectedCount = useMemo(
    () => applications.filter(a => statusOf(a) === 'rejected').length,
    [applications, statusOverrides],
  );
  const visibleApplications = useMemo(() => {
    const active = excludeApplicationIds
      ? applications.filter(a => !excludeApplicationIds.has(a.id))
      : applications;
    if (showRejected) return active;
    return active.filter(a => statusOf(a) !== 'rejected');
  }, [applications, excludeApplicationIds, showRejected, statusOverrides]);
  const selectedHidden = Boolean(selected && statusOf(selected) === 'rejected' && !showRejected);

  useEffect(() => {
    setStatusOverrides(prev => {
      const next = { ...prev };
      let changed = false;
      Object.keys(next).forEach(id => {
        const app = applications.find(a => a.id === id);
        if (!app || app.status === next[id]) {
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [applications]);

  useEffect(() => {
    if (selectedHidden && selectedApplicationId) onSelectApplicationId(null);
  }, [onSelectApplicationId, selectedApplicationId, selectedHidden]);

  const handleStatusChange = (applicationId: string, nextStatus: JobApplicationStatus) => {
    setStatusOverrides(prev => ({ ...prev, [applicationId]: nextStatus }));
    if (nextStatus === 'rejected' && !showRejected && applicationId === selectedApplicationId) {
      onSelectApplicationId(null);
    }
  };

  return (
    <div className={`people-hiring-candidates-grid${selected ? ' is-split' : ''}`}>
      <section className="people-hub-panel people-hiring-apps">
        <div className="people-hiring-section-head">
          <h3>
            Candidates {newCount > 0 && <span className="people-hiring-badge-new">{newCount} new</span>}
          </h3>
          <div className="people-hiring-apps-head-actions">
            <span className="people-hiring-muted">{visibleApplications.length} shown</span>
            {rejectedCount > 0 && (
              <button
                type="button"
                className="pm-btn pm-btn-ghost"
                aria-pressed={showRejected}
                onClick={() => setShowRejected(value => !value)}
              >
                {showRejected ? 'Hide rejected' : `Show rejected (${rejectedCount})`}
              </button>
            )}
            <button
              type="button"
              className="pm-btn pm-btn-secondary"
              onClick={() => setAddOpen(true)}
              disabled={postings.length === 0}
            >
              <Plus size={14} /> Add candidate
            </button>
          </div>
        </div>

        <div className="people-hiring-field" style={{ marginBottom: 12 }}>
          <label>Filter by posting</label>
          <select value={filterPostingId} onChange={e => onFilterPostingIdChange(e.target.value)}>
            <option value="">All postings</option>
            {postings.map(p => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        </div>

        {visibleApplications.length === 0 ? (
          <div className="people-hiring-empty">
            <p>{applications.length === 0 ? 'No applications yet.' : 'No active candidates.'}</p>
          </div>
        ) : (
          <div className="people-hiring-table-wrap">
            <table className="people-hiring-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Job</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Source</th>
                  <th>Heard from</th>
                </tr>
              </thead>
              <tbody>
                {visibleApplications.map(app => (
                  <tr
                    key={app.id}
                    className={selectedApplicationId === app.id ? 'people-hiring-row-selected' : ''}
                    data-application-id={app.id}
                    onClick={() => onSelectApplicationId(app.id)}
                  >
                    <td>
                      <strong>{app.firstName} {app.lastName}</strong>
                    </td>
                    <td>{app.jobTitle || '—'}</td>
                    <td>
                      <span className={`people-hiring-status people-hiring-status--${app.status === 'new' ? 'published' : app.status === 'rejected' ? 'closed' : 'draft'}`}>
                        {applicationStatusTrailLabel(app.status)}
                      </span>
                    </td>
                    <td>
                      {app.submittedAt.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </td>
                    <td>{SOURCE_LABELS[app.source] || app.source}</td>
                    <td>{app.heardFrom || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selected && !selectedHidden && (
        <section className="people-hub-panel people-hiring-candidate-detail">
          <JobApplicationDetail
            key={selected.id}
            application={selected}
            posting={postings.find(p => p.id === selected.jobPostingId) || null}
            reviewerUid={reviewerUid}
            highlightTeamCommentId={highlightTeamCommentId}
            onTeamCommentConsumed={onTeamCommentConsumed}
            highlightPdfCommentId={highlightPdfCommentId}
            onOpenResumePin={onOpenResumePin}
            onStatusChange={handleStatusChange}
          />
        </section>
      )}

      {addOpen && (
        <AddCandidateModal
          postings={postings}
          defaultPostingId={filterPostingId}
          onClose={() => setAddOpen(false)}
          onCreated={id => onSelectApplicationId(id)}
        />
      )}
    </div>
  );
};

export default JobApplicationsPanel;
